import React, { useState } from 'react';
import { css } from '@emotion/css';
import Button from '../../../components/general/Button';
import ConfirmationDialog from '../../../components/structure/ConfirmationDialog';
import WarningConfirmation from '../../../components/general/WarningConfirmation';
import { color_shades_darker, color_shades_light, dark_grey_blue } from '../../../constants/colors';

const editIcon = require('../../../content/images/pencil-blue.svg');
const deleteIcon = require('../../../content/images/delete-2-red.svg');

export interface TagListEntry {
    tagName: string
    displayName: string
    sentiment: string
    featureFlagId: string
    featureFlagLabel: string
}

interface TagListProps {
    foodOrderingTags: TagListEntry[]
    healthcareTags: TagListEntry[]
    /** Set of tag names that exist in both verticals */
    sharedTagNames: Set<string>
    isLoading: boolean
    onEdit: (tag: TagListEntry) => void
    onDelete: (tagName: string) => Promise<boolean>
    isDeleting: boolean
}

const TagList: React.FC<TagListProps> = (props) => {
    const [deleteTarget, setDeleteTarget] = useState<TagListEntry | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const isShared = deleteTarget ? props.sharedTagNames.has(deleteTarget.tagName) : false;

    const handleConfirmDelete = async () => {
        if (deleteTarget) {
            setDeleteError(null);
            const success = await props.onDelete(deleteTarget.tagName);
            if (success) {
                setDeleteTarget(null);
            } else {
                setDeleteError(`Failed to delete tag "${deleteTarget.displayName}". Please try again.`);
            }
        }
    };

    if (props.isLoading) {
        return <div className={loadingStyle}>Loading tags...</div>;
    }

    const hasFoodTags = props.foodOrderingTags.length > 0;
    const hasHealthcareTags = props.healthcareTags.length > 0;

    if (!hasFoodTags && !hasHealthcareTags) {
        return <div className={emptyStyle}>No custom tags found. Create one above to get started.</div>;
    }

    const renderTable = (title: string, entries: TagListEntry[]) => (
        <div className={tableContainerStyle}>
            <div className={tableTitleStyle}>{title}</div>
            {entries.length === 0 ? (
                <div className={tableEmptyStyle}>No tags for this vertical.</div>
            ) : (
                <>
                    <div className={listHeaderStyle}>
                        <span className={headerCellNameStyle}>Tag Name</span>
                        <span className={headerCellStyle}>Display Name</span>
                        <span className={headerCellActionsStyle}>Actions</span>
                    </div>
                    {entries.map(tag => (
                        <div key={tag.tagName} className={tagRowStyle}>
                            <span className={cellNameStyle} title={tag.tagName}>{tag.tagName}</span>
                            <span className={cellStyle} title={tag.displayName}>{tag.displayName}</span>
                            <span className={cellActionsStyle}>
                                <Button
                                    themes={['icon-small']}
                                    icon={editIcon}
                                    onClick={() => props.onEdit(tag)}
                                />
                                <Button
                                    themes={['icon-small']}
                                    icon={deleteIcon}
                                    onClick={() => setDeleteTarget(tag)}
                                />
                            </span>
                        </div>
                    ))}
                </>
            )}
        </div>
    );

    const deleteWarningText = deleteTarget
        ? isShared
            ? `Warning: "${deleteTarget.displayName}" (${deleteTarget.tagName}) exists in both Food Ordering and Healthcare. Deleting it will remove the tag from BOTH verticals. This action cannot be undone.`
            : `This will permanently remove the tag "${deleteTarget.displayName}" (${deleteTarget.tagName}) from all configuration. This action cannot be undone.`
        : '';

    return (
        <div>
            <div className={sideBySideStyle}>
                {renderTable('Food Ordering', props.foodOrderingTags)}
                {renderTable('Healthcare', props.healthcareTags)}
            </div>

            {deleteTarget &&
                <ConfirmationDialog
                    title="Delete Tag"
                    deleteText="Yes, Delete"
                    isLoading={props.isDeleting}
                    onClose={() => { setDeleteTarget(null); setDeleteError(null); }}
                    onConfirm={handleConfirmDelete}>
                    <WarningConfirmation
                        text={deleteWarningText}
                    />
                    {deleteError && <div className={deleteErrorStyle}>{deleteError}</div>}
                </ConfirmationDialog>
            }
        </div>
    );
};

const loadingStyle = css`
    font-family: Muli;
    font-size: 14px;
    color: ${color_shades_darker};
    padding: 24px;
    text-align: center;
`;

const emptyStyle = css`
    font-family: Muli;
    font-size: 14px;
    color: ${color_shades_darker};
    padding: 24px;
    text-align: center;
`;

const sideBySideStyle = css`
    display: flex;
    gap: 24px;
    align-items: flex-start;
`;

const tableContainerStyle = css`
    flex: 1;
    min-width: 0;
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    overflow: hidden;
`;

const tableTitleStyle = css`
    font-family: Muli;
    font-size: 14px;
    font-weight: 700;
    color: ${dark_grey_blue};
    padding: 12px 16px;
    background: #fafcfd;
    border-bottom: 1px solid ${color_shades_light};
`;

const tableEmptyStyle = css`
    font-family: Muli;
    font-size: 13px;
    color: ${color_shades_darker};
    padding: 24px 16px;
    text-align: center;
`;

const listHeaderStyle = css`
    display: flex;
    align-items: center;
    padding: 10px 16px;
    border-bottom: 2px solid ${color_shades_light};
    font-family: Muli;
    font-size: 11px;
    font-weight: 700;
    color: ${color_shades_darker};
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const headerCellNameStyle = css`
    flex: 2;
    min-width: 0;
`;

const headerCellStyle = css`
    flex: 1.5;
    min-width: 0;
`;

const headerCellActionsStyle = css`
    flex: 0 0 64px;
    text-align: right;
`;

const tagRowStyle = css`
    display: flex;
    align-items: center;
    padding: 10px 16px;
    border-bottom: 1px solid ${color_shades_light};
    font-family: Muli;
    font-size: 13px;
    color: ${dark_grey_blue};
    &:hover {
        background-color: #fafcfd;
    }
    &:last-child {
        border-bottom: none;
    }
`;

const cellNameStyle = css`
    flex: 2;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
    font-size: 12px;
`;

const cellStyle = css`
    flex: 1.5;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const cellActionsStyle = css`
    flex: 0 0 64px;
    display: flex;
    justify-content: flex-end;
    gap: 4px;
`;

const deleteErrorStyle = css`
    margin-top: 12px;
    padding: 10px 14px;
    background-color: rgba(216, 65, 77, 0.1);
    border: 1px solid #D8414D;
    border-radius: 5px;
    color: #D8414D;
    font-family: Muli;
    font-size: 13px;
`;

export default TagList;
